import { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from 'src/@/components/ui/sheet';
import { Textarea } from 'src/@/components/ui/textarea';
import QuestionRow from './QuestionRow';
import { CATALOGO } from './catalog';

function AddCustomDialog({ open, onOpenChange, onSubmit }) {
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');

    const reset = () => {
        setTitulo('');
        setDescricao('');
    };
    const handleClose = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };
    const handleSubmit = () => {
        const t = titulo.trim();
        if (!t) return;
        onSubmit({ titulo: t, descricao: descricao.trim() });
        handleClose(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Nova pergunta customizada</DialogTitle>
                    <DialogDescription>Específica para esta categoria do fluxo.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-2">
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Título</Label>
                        <Input
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            placeholder="Ex.: Aceita mobiliado?"
                            className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Descrição</Label>
                        <Textarea
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            rows={3}
                            className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => handleClose(false)} className="rounded-xl">
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!titulo.trim()}
                        className="rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                    >
                        Adicionar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function PerguntasNodeDrawer({
    open,
    onOpenChange,
    node,
    onChangeLabel,
    onChangePerguntas,
    onDeleteNode,
    getEssencialDisabled,
    getQualifDisabled,
}) {
    const [showAddDialog, setShowAddDialog] = useState(false);
    const perguntas = node?.data?.perguntas || [];
    const label = node?.data?.label || '';

    const getPergunta = (id) => perguntas.find((p) => p.id === id);
    const customList = perguntas.filter((p) => p.custom);

    const updateState = (id, patch, extras = {}) => {
        const existing = getPergunta(id);
        // `extras` carries catalog-derived metadata (e.g. spinField) that must
        // be applied on every update, not only on first creation — backfills
        // old entries. `patch` wins over extras (so user-set `script` sticks).
        const next = { ...(existing || { id }), ...extras, ...patch };

        if (patch.ativa === false) next.qualificatoria = false;
        if (patch.qualificatoria === true && !next.ativa) return;

        if (existing) {
            onChangePerguntas(perguntas.map((p) => (p.id === id ? next : p)));
        } else {
            onChangePerguntas([...perguntas, next]);
        }
    };

    const addCustom = ({ titulo, descricao }) => {
        const id = `custom_${Date.now()}`;
        onChangePerguntas([
            ...perguntas,
            { id, custom: true, titulo, descricao, ativa: false, qualificatoria: false },
        ]);
    };

    const removeCustom = (id) => {
        onChangePerguntas(perguntas.filter((p) => p.id !== id));
    };

    if (!node) return null;

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="right" className="w-[90%] sm:max-w-xl p-0 flex flex-col">
                    <SheetHeader className="px-6 py-4 border-b">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                                <Iconify icon={node?.data?.icon || 'mdi:home-outline'} width={20} className="text-violet-600" />
                            </div>
                            <div className="flex-1">
                                <SheetTitle className="text-left">{label || 'Tipo de imóvel'}</SheetTitle>
                                <SheetDescription className="text-left">
                                    Perguntas específicas desse tipo de imóvel. Só serão feitas quando o lead cair neste ramo do fluxo.
                                </SheetDescription>
                            </div>
                        </div>
                    </SheetHeader>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        <ScrollArea className="flex-1">
                            <div className="p-6 flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900">Perguntas</h4>
                                        <p className="text-xs text-gray-500">Ative as perguntas que devem ser feitas para este tipo de imóvel.</p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAddDialog(true)}
                                        className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED]"
                                    >
                                        <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                                        Adicionar
                                    </Button>
                                </div>

                                <div className="flex flex-col divide-y divide-gray-100">
                                    {CATALOGO.map((item) => {
                                        const state = getPergunta(item.id);
                                        return (
                                            <QuestionRow
                                                key={item.id}
                                                icon={item.icon}
                                                titulo={item.titulo}
                                                descricao={item.descricao}
                                                state={state}
                                                essencialDisabled={getEssencialDisabled(state)}
                                                qualificatoriaDisabled={getQualifDisabled(state)}
                                                spinField={item.spinField}
                                                defaultScript={item.defaultScript}
                                                onChange={(patch) => updateState(item.id, patch, { spinField: item.spinField })}
                                            />
                                        );
                                    })}
                                    {customList.map((p) => (
                                        <QuestionRow
                                            key={p.id}
                                            icon="mdi:comment-question-outline"
                                            titulo={p.titulo}
                                            descricao={p.descricao}
                                            state={p}
                                            essencialDisabled={getEssencialDisabled(p)}
                                            qualificatoriaDisabled={getQualifDisabled(p)}
                                            onChange={(patch) => updateState(p.id, patch, { custom: true, titulo: p.titulo, descricao: p.descricao })}
                                            onDelete={() => removeCustom(p.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </ScrollArea>
                    </div>

                    <div className="px-6 py-4 border-t bg-gray-50/80 flex justify-between">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onDeleteNode}
                            className="rounded-xl text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                            <Iconify icon="eva:trash-2-outline" width={16} className="mr-1" />
                            Remover do fluxo
                        </Button>
                        <Button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                        >
                            Concluir
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            <AddCustomDialog open={showAddDialog} onOpenChange={setShowAddDialog} onSubmit={addCustom} />
        </>
    );
}
